import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import css from "./main.module.css";
import filteraiLin from "../../img/filteraiLin.png";
import OneLessons from "./oneLessons";
import { getVideoCategories } from "../../lib/videoCategoryApi";
import { getLanguageLevels } from "../../lib/getLanguageLevels";

const PopularVideo = ({ category, lessons, allLevels }) => {
  const { t, i18n } = useTranslation();


  const [categories, setCategories] = useState([]);
  const [levels, setLevels] = useState(
    JSON.parse(localStorage.getItem("languageLevels")) || []
  );
  const [selectedLevel, setSelectedLevel] = useState("all");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [itemsToShow, setItemsToShow] = useState(5);
  const levelMapping = {
    "pre-A1": "Pre-Beginner",
    A1: t("levelonea"),
    A2: t("leveltwoa"),
    B1: t("levevoneb"),
    B2: t("lebeltwob"),
    C1: t("levelonec"),
    C2: t("leveltwoc"),
  };
  const levelOrder = ["pre-A1", "A1", "A2", "B1", "B2", "C1", "C2"];

  useEffect(() => {
    setShowMore(false);
  }, [selectedLevel]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesData = await getVideoCategories();
        setCategories(categoriesData.results);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchLevels = async () => {
      try {
        const levelsData = await getLanguageLevels();
        const adaptedLevels = levelOrder
          .map((key) => levelsData.results.find((level) => level.name === key))
          .filter(Boolean)
          .map((level) => ({
            id: String(level.id),
            name: levelMapping[level.name] || level.name,
          }));

        setLevels([{ id: "all", name: t("allLevels") }, ...adaptedLevels]);
        localStorage.setItem("languageLevels", JSON.stringify(adaptedLevels));
      } catch (error) {
        console.error("Error fetching levels:", error);
      }
    };

    if (!levels.length) {
      fetchLevels();
    } else {
      setLevels([{ id: "all", name: t("allLevels") }, ...levels]);
    }
  }, [i18n.language]);

  const filteredLessons = useMemo(() => {
    const filtered =
      selectedLevel === "all"
        ? lessons
        : lessons.filter(
            (lesson) => String(lesson.video.languageLevel) === selectedLevel
          );

    return showMore ? filtered : filtered.slice(0, itemsToShow);
  }, [selectedLevel, lessons, showMore, itemsToShow]);

  const getCategoryName = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.name : "Unknown Category";
  };

  const handleLevelChange = (level) => {
    setSelectedLevel(level);
    setShowDropdown(false);
  };

  const handleShowMore = () => {
    setShowMore(true);
  };

  const handleShowLess = () => {
    setShowMore(false);
  };

  const hasMoreItems = useMemo(() => {
    const totalFiltered =
      selectedLevel === "all"
        ? lessons.length
        : lessons.filter(
            (lesson) => String(lesson.video.languageLevel) === selectedLevel
          ).length;

    return totalFiltered > itemsToShow;
  }, [selectedLevel, lessons, itemsToShow]);

  if (lessons.length === 0) {
    return null;
  }

  return (
    <div className={css.popularWrap}>
      <div className={css.popularWrapSmall}>
        <div className={css.wrapPopWithLevel}>
          <p className={css.videoCatName}>{category.name}</p>
          {hasMoreItems && !showMore && (
            <button className={css.showMoreBtn} onClick={handleShowMore}>
              {t("showMore") || "Show More"}
            </button>
          )}
          {showMore && (
            <button className={css.showLessBtn} onClick={handleShowLess}>
              {t("showLess") || "Show Less"}
            </button>
          )}
        </div>

        <div className={showMore ? css.wrapLessonsExpanded : css.wrapLessons}>
          {filteredLessons.map((lesson, idx) => (
            <div key={lesson.id} className={css.lessonItem}>
              <OneLessons
                allLevels={allLevels}
                lesson={lesson}
                getCategoryName={getCategoryName}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PopularVideo;
